import { useTranslation } from 'react-i18next'
import type { Attendance, Student } from '../../types/entities'

export interface AttendanceCounts {
  total: number
  marked: number
  present: number
  absent: number
  late: number
  sick: number
  excused: number
  unexcused: number
}

/** Считает агрегаты по marks для текущего класса/даты. */
export function computeAttendanceCounts(
  students: Student[],
  marksByStudent: Map<string, Attendance>,
): AttendanceCounts {
  const out: AttendanceCounts = {
    total: students.length, marked: 0, present: 0, absent: 0,
    late: 0, sick: 0, excused: 0, unexcused: 0,
  }
  for (const s of students) {
    const m = marksByStudent.get(s.id)?.mark
    if (m === 'present') out.present++
    else if (m === 'absent') out.absent++
    else if (m === 'late') out.late++
    else if (m === 'sick') out.sick++
    else if (m === 'excused') out.excused++
    else if (m === 'unexcused') out.unexcused++
  }
  out.marked = out.present + out.absent + out.late + out.sick + out.excused + out.unexcused
  return out
}

/** Цветные карточки-счётчики по типам отметок. */
export function AttendanceStats({ counts }: { counts: AttendanceCounts }) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-5 text-center text-xs">
      <StatChip color="green" label={t('attendanceStats.statPresent')} count={counts.present} />
      <StatChip color="yellow" label={t('attendanceStats.statLate')} count={counts.late} />
      <StatChip color="red" label={t('attendanceStats.statAbsent')} count={counts.absent} />
      <StatChip color="blue" label={t('attendanceStats.statSick')} count={counts.sick} />
      <StatChip color="violet" label={t('attendanceStats.statExcused')} count={counts.excused} />
      <StatChip color="gray" label={t('attendanceStats.statWithout', { marked: counts.total - counts.marked, total: counts.total })} count={counts.unexcused} />
    </div>
  )
}

/** Расшифровка обозначений (легенда внизу страницы). */
export function AttendanceLegend() {
  const { t } = useTranslation()
  return (
    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600">
      <LegendItem label="+"  color="bg-green-100 text-green-700 border-green-300"   text={t('attendanceStats.textPresent')} />
      <LegendItem label={t('attendanceStats.codeLate')} color="bg-yellow-100 text-yellow-700 border-yellow-300" text={t('attendanceStats.textLate')} />
      <LegendItem label={t('attendanceStats.codeAbsent')}  color="bg-red-100 text-red-700 border-red-300"         text={t('attendanceStats.textAbsent')} />
      <LegendItem label={t('attendanceStats.codeSick')}  color="bg-blue-100 text-blue-700 border-blue-300"      text={t('attendanceStats.textSick')} />
      <LegendItem label={t('attendanceStats.codeExcused')} color="bg-violet-100 text-violet-700 border-violet-300" text={t('attendanceStats.textExcused')} />
      <LegendItem label={t('attendanceStats.codeUnexcused')} color="bg-gray-100 text-gray-700 border-gray-300"      text={t('attendanceStats.textUnexcused')} />
    </div>
  )
}

function StatChip({
  label,
  count,
  color,
}: {
  label: string
  count: number
  color: 'green' | 'yellow' | 'red' | 'blue' | 'violet' | 'gray'
}) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  }
  return (
    <div className={`${colors[color]} border rounded-lg px-2 py-1.5`}>
      <div className="text-lg font-bold">{count}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  )
}

function LegendItem({ label, color, text }: { label: string; color: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-7 h-6 rounded text-[11px] font-semibold border text-center leading-6 ${color}`}>
        {label}
      </span>
      <span>— {text}</span>
    </span>
  )
}
